import { Check, RotateCw } from 'lucide-react';
import type { EducateStoryline } from '@/types/educate';

interface CompleteScreenProps {
  storyline: EducateStoryline;
  onRestart: () => void;
}

export function CompleteScreen({ storyline, onRestart }: CompleteScreenProps) {
  return (
    <div className="flex min-h-[70vh] w-full items-center justify-center px-4 py-10">
      <div
        className="w-full max-w-3xl rounded-2xl bg-white px-8 py-10 text-center shadow-sm ring-1 ring-slate-200 sm:px-14 sm:py-12"
        data-testid="complete-screen"
      >
        <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500">
          <Check size={44} strokeWidth={3} className="text-white" />
        </div>

        <h1 className="text-3xl font-semibold text-slate-800 sm:text-4xl">Module complete</h1>
        <p className="mt-2 text-base font-medium text-slate-600 sm:text-lg">
          Learning module finished
        </p>

        <div className="mt-8 rounded-xl border-l-4 border-emerald-500 bg-emerald-50 p-6 text-left">
          <p className="text-base font-medium text-slate-800">{storyline.uiContent.title}</p>
          <p className="mt-1 text-sm text-slate-500">
            Your progress has been recorded and you can restart this module anytime to review the
            content.
          </p>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Status</p>
            <p className="mt-1 text-base font-semibold text-emerald-600">Completed</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Progress</p>
            <p className="mt-1 text-base font-semibold text-emerald-600">100%</p>
          </div>
        </div>

        <button
          type="button"
          onClick={onRestart}
          className="mx-auto mt-8 flex w-full max-w-xs items-center justify-center gap-2 rounded-lg bg-emerald-500 px-4 py-3.5 text-base font-semibold text-white transition hover:bg-emerald-600"
        >
          <RotateCw size={18} />
          Restart module
        </button>

        <p className="mt-6 text-xs leading-relaxed text-slate-500">
          You can review this module as many times as you need. Restarting will reset your progress
          through the content.
        </p>
      </div>
    </div>
  );
}
